import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import utc from 'dayjs/plugin/utc';
import {
  CustomFieldCondition,
  CustomFieldConditionValue,
  CustomFieldFilterMetadata,
  CustomFieldOperatorMetadata,
  CustomFieldValueShape,
} from './typings';
import { CustomFieldsData } from '../../../typings';

export const getCustomFieldOptionName = (
  customFieldsData: CustomFieldsData | undefined,
  fieldId: string,
  optionId: string
): string | undefined => {
  if (!customFieldsData) return undefined;
  const all = [...(customFieldsData.customFields ?? []), ...(customFieldsData.internalFields ?? [])];
  const cf = all.find((f) => f.id === fieldId);
  if (!cf || !('list' in cf) || !Array.isArray(cf.list)) return undefined;
  return cf.list.find((opt) => opt.id === optionId)?.name;
};

dayjs.extend(utc);
dayjs.extend(advancedFormat);

export const countSelectedOptions = (condition: CustomFieldCondition): number => {
  const v = condition.value;
  if (v.type === 'VALUE' && Array.isArray(v.value)) return v.value.length;
  return 1;
};

export const filterCompleteConditions = (conditions: CustomFieldCondition[]): CustomFieldCondition[] => {
  return conditions.filter((c) => {
    if (!c.id || !c.operator || !c.value?.type) return false;
    if (c.value.type === 'NO_VALUE') return true;
    if (c.value.type === 'VALUE') {
      const v = c.value.value;
      if (v === undefined || v === null) return false;
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === 'string') return v.trim().length > 0;
      return true;
    }
    if (c.value.type === 'BETWEEN') {
      const startOk = c.value.start !== undefined && c.value.start !== null && c.value.start !== '';
      const endOk = c.value.end !== undefined && c.value.end !== null && c.value.end !== '';
      return startOk && endOk && c.value.start < c.value.end;
    }
    if (c.value.type === 'DATE_PERIOD') {
      return typeof c.value.value === 'number' && c.value.value > 0 && !!c.value.suffix;
    }
    return false;
  });
};

export const defaultValueForShape = (
  shape: CustomFieldValueShape,
  suffix?: CustomFieldOperatorMetadata['suffix']
): CustomFieldConditionValue => {
  if (shape === 'NO_VALUE') return { type: 'NO_VALUE' };
  if (shape === 'BETWEEN') return { type: 'BETWEEN', start: '', end: '' };
  if (shape === 'DATE_PERIOD') return { type: 'DATE_PERIOD', value: 0, suffix: suffix?.[0] ?? 'days' };
  return { type: 'VALUE', value: '' };
};

export const preserveValueForOperator = (
  prev: CustomFieldConditionValue,
  next: CustomFieldOperatorMetadata
): CustomFieldConditionValue => {
  const shape = next.valueType;
  const fallback = defaultValueForShape(shape, next.suffix);

  if (shape === 'NO_VALUE') return fallback;

  if (shape === 'VALUE') {
    if (prev.type === 'VALUE') return prev;
    if (prev.type === 'BETWEEN') {
      const start = prev.start;
      if (start === '' || start === undefined || start === null) return fallback;
      return { type: 'VALUE', value: start };
    }
    return fallback;
  }

  if (shape === 'BETWEEN') {
    if (prev.type === 'BETWEEN') return prev;
    if (prev.type === 'VALUE') {
      const v = prev.value;
      if (typeof v === 'string' || typeof v === 'number') {
        return { type: 'BETWEEN', start: v, end: '' };
      }
    }
    return fallback;
  }

  if (shape === 'DATE_PERIOD') {
    if (prev.type === 'DATE_PERIOD') {
      const suffixes = next.suffix ?? [];
      const suffix = suffixes.includes(prev.suffix) ? prev.suffix : suffixes[0] ?? prev.suffix;
      return { type: 'DATE_PERIOD', value: prev.value, suffix };
    }
    return fallback;
  }

  return fallback;
};

export const serializeConditionsForWire = (conditions: CustomFieldCondition[]): CustomFieldCondition[] => {
  return filterCompleteConditions(conditions);
};

const formatDateValue = (iso: string | number): string => {
  if (typeof iso !== 'string' || iso.length === 0) return '';
  const d = dayjs.utc(iso);
  if (!d.isValid()) return String(iso);
  return d.format('Do MMM YYYY');
};

export const formatValueSummary = (
  condition: CustomFieldCondition,
  getOptionName?: (optionId: string) => string | undefined
): string => {
  const v = condition.value;
  if (v.type === 'NO_VALUE') return '';
  if (v.type === 'BETWEEN') {
    if (condition.type === 'date') return `${formatDateValue(v.start)} - ${formatDateValue(v.end)}`;
    return `${v.start} - ${v.end}`;
  }
  if (v.type === 'DATE_PERIOD') return `${v.value} ${v.suffix}`;
  const raw = v.value;
  if (Array.isArray(raw)) {
    if (condition.type === 'single_select' || condition.type === 'multi_select') {
      const names = raw.map((id) => getOptionName?.(id) ?? id);
      if (names.length === 1) return names[0];
      return `${names.length} selected`;
    }
    return raw.length === 1 ? String(raw[0]) : `${raw.length} selected`;
  }
  if (typeof raw === 'boolean') return raw ? 'True' : 'False';
  if (condition.type === 'date' && typeof raw === 'string') return formatDateValue(raw);
  return String(raw ?? '');
};

export const conditionsSignature = (conditions: CustomFieldCondition[]): string => {
  return JSON.stringify(filterCompleteConditions(conditions));
};

const toBase64Url = (input: string): string => {
  const b64 = typeof window !== 'undefined' ? window.btoa(unescape(encodeURIComponent(input))) : '';
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const fromBase64Url = (input: string): string => {
  if (typeof window === 'undefined') return '';
  const padded = input.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((input.length + 3) % 4);
  return decodeURIComponent(escape(window.atob(padded)));
};

export const encodeConditionsForUrl = (conditions: CustomFieldCondition[]): string => {
  const complete = filterCompleteConditions(conditions);
  if (complete.length === 0) return '';
  return toBase64Url(JSON.stringify(complete));
};

const isValidShape = (value: unknown): value is CustomFieldConditionValue => {
  if (!value || typeof value !== 'object') return false;
  const v = value as { type?: string };
  return v.type === 'NO_VALUE' || v.type === 'VALUE' || v.type === 'BETWEEN' || v.type === 'DATE_PERIOD';
};

export const decodeConditionsFromUrl = (
  encoded: string,
  metadata?: CustomFieldFilterMetadata | null
): CustomFieldCondition[] => {
  if (!encoded) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(fromBase64Url(encoded));
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  const fieldById = new Map((metadata?.fields ?? []).map((f) => [f.id, f]));
  const operatorByFieldType = metadata?.dataTypeOperator ?? {};

  const valid: CustomFieldCondition[] = [];
  for (const raw of parsed) {
    if (!raw || typeof raw !== 'object') continue;
    const c = raw as Partial<CustomFieldCondition>;
    if (!c.id || !c.operator || !c.type || !isValidShape(c.value)) continue;

    if (metadata) {
      const field = fieldById.get(c.id);
      if (!field || !field.filterable) continue;
      const allowedOps = (operatorByFieldType[field.dataType] ?? []).map((o) => o.type);
      if (!allowedOps.includes(c.operator)) continue;
    }

    valid.push({
      id: c.id,
      type: c.type,
      operator: c.operator,
      value: c.value as CustomFieldConditionValue,
    });
  }
  return filterCompleteConditions(valid);
};
